/**
 * Suggested question item container component
 * @remarks
 * Manages the callback logic for suggested question item.
 * Memoized to prevent recreation on each render and avoid
 * new function props to child component.
 */

import { memo, useCallback } from "react";

import { SuggestedQuestionItemView } from "./SuggestedQuestionItemView";

/**
 * Suggested question data structure
 */
interface SuggestedQuestion {
  icon: string;
  text: string;
  query: string;
}

/**
 * Props for SuggestedQuestionItemContainer component
 */
interface SuggestedQuestionItemContainerProps {
  question: SuggestedQuestion;
  onPress: (query: string) => void;
}

/**
 * Memoized suggested question item container
 *
 * Extracted to avoid creating new functions on each render.
 * @remarks Theme (light/dark) and responsive width are now handled by the
 * View's semantic tokens + screen-width breakpoints (SE-5380), so the
 * previously-forwarded isDark/windowWidth props were removed.
 */
const SuggestedQuestionItemContainer = memo(
  function SuggestedQuestionItemContainer({
    question,
    onPress,
  }: SuggestedQuestionItemContainerProps) {
    const handlePress = useCallback(() => {
      onPress(question.query);
    }, [onPress, question.query]);

    return (
      <SuggestedQuestionItemView question={question} onPress={handlePress} />
    );
  }
);

SuggestedQuestionItemContainer.displayName = "SuggestedQuestionItemContainer";

export default SuggestedQuestionItemContainer;
